import type { Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { rememberReturnTo } from './auth-intent';

type AuthState = {
  loading: boolean;
  configured: boolean;
  session: Session | null;
  user: User | null;
  signInWithGoogle: (returnTo?: string) => Promise<void>;
  signInWithApple: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(Boolean(supabase));

  useEffect(() => {
    if (!supabase) return;

    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthState>(() => {
    const signInWithProvider = async (provider: 'google' | 'apple', returnTo?: string) => {
      if (!supabase) throw new Error('Web sign-in is not configured yet.');
      rememberReturnTo(returnTo);
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
    };
    return {
      configured: Boolean(supabase),
      loading,
      session,
      user: session?.user ?? null,
      signInWithGoogle: (returnTo) => signInWithProvider('google', returnTo),
      signInWithApple: (returnTo) => signInWithProvider('apple', returnTo),
      signOut: async () => {
        if (!supabase) return;
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      },
    };
  }, [loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider.');
  return context;
}
